import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  MessageSquare, 
  Check, 
  X, 
  Trash2, 
  Loader2,
  ArrowLeft,
  Clock,
  User,
  BookOpen,
  Filter,
  AlertCircle,
  CheckCircle,
  XCircle
} from 'lucide-react';

const STATUS_CONFIG = {
  pending: { label: 'Pendente', color: 'bg-yellow-500/20 text-yellow-400', icon: Clock },
  approved: { label: 'Aprovado', color: 'bg-green-500/20 text-green-400', icon: CheckCircle },
  rejected: { label: 'Rejeitado', color: 'bg-red-500/20 text-red-400', icon: XCircle }
};

export default function AdminComments() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [comments, setComments] = useState([]);
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  
  // Reject dialog
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedComment, setSelectedComment] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/');
      return;
    }
    loadData();
  }, [user, navigate, statusFilter]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, commentsRes] = await Promise.all([
        api.get('/admin/comments/stats'),
        api.get(`/admin/comments?status=${statusFilter}&limit=100`)
      ]);
      setStats(statsRes.data);
      setComments(commentsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar comentários');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (comment) => {
    setProcessing(true);
    try {
      await api.post(`/admin/comments/${comment.id}/moderate`, { action: 'approve' });
      toast.success('Comentário aprovado');
      loadData();
    } catch (error) {
      toast.error('Erro ao aprovar comentário');
    } finally {
      setProcessing(false);
    }
  };

  const openRejectDialog = (comment) => {
    setSelectedComment(comment);
    setRejectionReason('');
    setRejectDialogOpen(true);
  };

  const handleReject = async () => {
    if (!selectedComment) return;
    
    setProcessing(true);
    try {
      await api.post(`/admin/comments/${selectedComment.id}/moderate`, { 
        action: 'reject',
        rejection_reason: rejectionReason || null
      });
      toast.success('Comentário rejeitado');
      setRejectDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Erro ao rejeitar comentário');
    } finally {
      setProcessing(false);
    }
  };

  const handleDelete = async (commentId) => {
    if (!confirm('Eliminar este comentário permanentemente?')) return;
    
    try {
      await api.delete(`/admin/comments/${commentId}`);
      toast.success('Comentário eliminado');
      loadData();
    } catch (error) {
      toast.error('Erro ao eliminar comentário');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('pt-PT', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-ruby" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="max-w-6xl mx-auto px-4 pt-24 pb-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/admin')}
              className="text-muted-foreground hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-ruby/20 flex items-center justify-center">
                <MessageSquare className="w-6 h-6 text-ruby" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">Moderação de Comentários</h1>
                <p className="text-muted-foreground">Aprovar ou rejeitar comentários dos utilizadores</p>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-surface border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Pendentes</p>
                  <p className="text-2xl font-bold text-yellow-400">{stats.pending}</p>
                </div>
                <Clock className="w-8 h-8 text-yellow-400/50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-surface border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Aprovados</p>
                  <p className="text-2xl font-bold text-green-400">{stats.approved}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-400/50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-surface border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Rejeitados</p>
                  <p className="text-2xl font-bold text-red-400">{stats.rejected}</p>
                </div>
                <XCircle className="w-8 h-8 text-red-400/50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-surface border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total</p>
                  <p className="text-2xl font-bold text-foreground">{stats.total}</p>
                </div>
                <MessageSquare className="w-8 h-8 text-white/50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filter */}
        <Card className="bg-surface border-border mb-6">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[200px] bg-background border-border">
                  <SelectValue placeholder="Filtrar por estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pendentes</SelectItem>
                  <SelectItem value="approved">Aprovados</SelectItem>
                  <SelectItem value="rejected">Rejeitados</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-sm text-muted-foreground">
                {comments.length} comentários
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Comments List */}
        {comments.length === 0 ? (
          <Card className="bg-surface border-border">
            <CardContent className="py-16 text-center">
              <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-medium text-foreground mb-2">
                Nenhum comentário {statusFilter === 'pending' ? 'pendente' : statusFilter === 'approved' ? 'aprovado' : 'rejeitado'}
              </h3>
              <p className="text-muted-foreground">
                {statusFilter === 'pending' 
                  ? 'Não há comentários à espera de moderação' 
                  : 'Muda o filtro para ver outros comentários'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {comments.map((comment) => {
              const statusConfig = STATUS_CONFIG[comment.status] || STATUS_CONFIG.pending;
              const StatusIcon = statusConfig.icon;
              
              return (
                <Card key={comment.id} className="bg-surface border-border">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        {/* Header */}
                        <div className="flex items-center gap-3 mb-3">
                          <Badge className={statusConfig.color}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {statusConfig.label}
                          </Badge>
                          <span className="text-sm text-muted-foreground flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {comment.user_name}
                          </span>
                          <span className="text-sm text-muted-foreground flex items-center gap-1">
                            <BookOpen className="w-3 h-3" />
                            {comment.story_title || 'História'}
                          </span>
                          <span className="text-sm text-muted-foreground">
                            {formatDate(comment.created_at)}
                          </span>
                        </div>
                        
                        {/* Content */}
                        <div className="p-4 bg-white/5 rounded-lg mb-3">
                          <p className="text-muted-foreground whitespace-pre-wrap">{comment.content}</p>
                        </div>
                        
                        {/* Rejection reason */}
                        {comment.status === 'rejected' && comment.rejection_reason && (
                          <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                            <p className="text-sm text-red-400">
                              <strong>Motivo da rejeição:</strong> {comment.rejection_reason}
                            </p>
                          </div>
                        )}
                      </div>
                      
                      {/* Actions */}
                      <div className="flex gap-2 shrink-0">
                        {comment.status === 'pending' && (
                          <>
                            <Button
                              size="sm"
                              onClick={() => handleApprove(comment)}
                              disabled={processing}
                              className="bg-green-600 hover:bg-green-700"
                              data-testid={`approve-comment-${comment.id}`}
                            >
                              <Check className="w-4 h-4 mr-1" />
                              Aprovar
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openRejectDialog(comment)}
                              disabled={processing}
                              className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                              data-testid={`reject-comment-${comment.id}`}
                            >
                              <X className="w-4 h-4 mr-1" />
                              Rejeitar
                            </Button>
                          </>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDelete(comment.id)}
                          className="border-border text-muted-foreground hover:text-red-400 hover:border-red-500/30"
                          data-testid={`delete-comment-${comment.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Reject Dialog */}
        <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
          <DialogContent className="bg-surface border-border max-w-lg z-50">
            <DialogHeader>
              <DialogTitle className="text-foreground flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-400" />
                Rejeitar Comentário
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="p-4 bg-white/5 rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Comentário de {selectedComment?.user_name}:</p>
                <p className="text-muted-foreground">{selectedComment?.content}</p>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">
                  Motivo da rejeição (opcional)
                </label>
                <Textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Ex: Comentário ofensivo, spam, conteúdo inapropriado..."
                  className="bg-background border-border"
                />
                <p className="text-xs text-muted-foreground">
                  O utilizador será notificado sobre a rejeição e o motivo (se fornecido).
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => setRejectDialogOpen(false)} 
                className="border-border"
              >
                Cancelar
              </Button>
              <Button 
                onClick={handleReject}
                disabled={processing}
                className="bg-red-600 hover:bg-red-700"
              >
                {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <X className="w-4 h-4 mr-2" />}
                Rejeitar Comentário
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
